import React, { useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { AiSuggestionsEntryPointValue } from '@atlaskit/editor-common/analytics/ai-suggestions-entry-points';
import { Text } from '@atlaskit/primitives/compiled';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { UNSAFE_UpdateOnChange as SpotlightUpdateOnChange } from '@atlaskit/spotlight/update-on-change';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useMessageData } from '@atlassian/post-office-headless-api/hooks/useMessageData';

import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import reviewNudgeMedia from './assets/review-nudge-media.svg';
// Editor AI's headless Post Office template (post-office/integrated-teams/editor-ai).
const MESSAGE_TEMPLATE_ID = 'editor-ai-content-reviewer-contextual';

const styles = cssMap({
	media: {
		display: 'block',
		height: '135px',
		width: '295px',
	},
});

const i18n = defineMessages({
	title: {
		id: 'object-sidebar-components.review-nudge.contextual.title',
		defaultMessage: 'Polish your page for impact',
		description:
			'Title of the spotlight that introduces AI suggested edits from the Review suggestions button in the object sidebar',
	},
	body: {
		id: 'object-sidebar-components.review-nudge.contextual.body',
		defaultMessage:
			'Get suggestions from the Content Reviewer for clearer writing, structure and accuracy.',
		description:
			'Body text of the spotlight that introduces AI suggested edits from the Review suggestions button in the object sidebar. Content Reviewer is the name of the AI agent that reviews the page',
	},
	cta: {
		id: 'object-sidebar-components.review-nudge.contextual.cta',
		defaultMessage: 'Get suggestions',
		description:
			'Button in the spotlight that introduces AI suggested edits; starts a review of the page',
	},
});

type ContextualNudgeCardProps = {
	dismissMessage: (messageInstanceId: string) => void;
	messageInstanceId: string;
	onGetSuggestions?: () => void;
	onShown?: () => void;
	/** Called when the nudge leaves the screen, whether dismissed or unmounted. */
	onHidden?: () => void;
	subscribeToTargetClick?: (listener: () => void) => () => void;
};

const ContextualNudgeCard = ({
	dismissMessage,
	messageInstanceId,
	onGetSuggestions,
	onShown,
	onHidden,
	subscribeToTargetClick,
}: ContextualNudgeCardProps) => {
	const { formatMessage } = useIntl();
	const { onCtaActioned, onClosed } = usePostOfficeUserInteractions();
	const { PopoverContent } = useChoreographerAssets();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();

	const hasRespondedRef = useRef(false);

	useEffect(() => {
		onShown?.();
		return () => onHidden?.();
	}, [onShown, onHidden]);

	// The close button, Escape and the CTA can race; only record one response.
	const handleClose = useCallback(() => {
		if (hasRespondedRef.current) {
			return;
		}
		hasRespondedRef.current = true;
		void onClosed({ interactionIdentifier: 'close' });
		dismissMessage(messageInstanceId);
	}, [dismissMessage, messageInstanceId, onClosed]);

	// The user got to the Review button themselves, so the nudge has nothing left to ask.
	const handleAcknowledge = useCallback(() => {
		if (hasRespondedRef.current) {
			return;
		}
		hasRespondedRef.current = true;
		void onCtaActioned({ interactionIdentifier: 'review-button' });
		dismissMessage(messageInstanceId);
	}, [dismissMessage, messageInstanceId, onCtaActioned]);

	useEffect(
		() => subscribeToTargetClick?.(handleAcknowledge),
		[subscribeToTargetClick, handleAcknowledge],
	);

	// PopoverContent only closes on Escape while focus is inside it, and focus isn't moved here.
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				handleClose();
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [handleClose]);

	const handleCta = useCallback(() => {
		if (hasRespondedRef.current) {
			return;
		}
		hasRespondedRef.current = true;
		void onCtaActioned({ interactionIdentifier: 'start-review' });
		dismissMessage(messageInstanceId);
		const aiSuggestions = fullPageEditorPresetAPI?.aiSuggestions;
		if (aiSuggestions) {
			// Starts the review in place, without opening the Suggestions panel.
			aiSuggestions.actions.setThinkingState('thinking', {
				entryPoint: AiSuggestionsEntryPointValue.OBJECT_SIDEBAR_CONTROL,
			});
		} else {
			// No editor to review in (a published page), so use the Review button's own action.
			onGetSuggestions?.();
		}
	}, [
		dismissMessage,
		fullPageEditorPresetAPI?.aiSuggestions,
		messageInstanceId,
		onCtaActioned,
		onGetSuggestions,
	]);

	return (
		<>
			<SpotlightUpdateOnChange />
			<PopoverContent
				dismiss={handleClose}
				isVisible
				offset={[0, 8]}
				placement="left-start"
				shouldDismissOnClickOutside={false}
			>
				<SpotlightCard testId="review-button-nudge-contextual-card">
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(i18n.title)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={handleClose} autoFocus={false} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightMedia>
						<img css={styles.media} alt="" src={reviewNudgeMedia} />
					</SpotlightMedia>
					<SpotlightBody>
						<Text>{formatMessage(i18n.body)}</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={handleCta} appearance="primary">
								{formatMessage(i18n.cta)}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</>
	);
};

export type ReviewContextualNudgeProps = {
	/** Called once the nudge is on screen. */
	onShown?: () => void;
	/** Called when the nudge leaves the screen, whether dismissed or unmounted. */
	onHidden?: () => void;
	/** Called by the call to action when there is no editor to start the review in directly. */
	onGetSuggestions?: () => void;
	/** Subscribes to clicks on the Review button; the nudge is acknowledged when one arrives. */
	subscribeToTargetClick?: (listener: () => void) => () => void;
};

/**
 * The "Polish your page for impact" contextual nudge. It renders the Post Office message, and rendering
 * that message records an exposure, so mount it only for users who are eligible to see it. It is
 * anchored to the Review suggestions button through the enclosing spotlight
 * `PopoverProvider`/`PopoverTarget` and Post Office placement provider.
 */
export const ReviewContextualNudge = ({
	onGetSuggestions,
	onShown,
	onHidden,
	subscribeToTargetClick,
}: ReviewContextualNudgeProps): React.JSX.Element | null => {
	const { message, PostOfficeMessage, dismissMessage } = useMessageData(
		{ messageTemplateId: MESSAGE_TEMPLATE_ID },
		{ dataOutsideWrapper: true },
	);

	if (!message || !PostOfficeMessage) {
		return null;
	}

	return (
		<PostOfficeMessage>
			{() => (
				<ContextualNudgeCard
					dismissMessage={dismissMessage}
					messageInstanceId={message.messageInstanceId}
					onGetSuggestions={onGetSuggestions}
					onShown={onShown}
					onHidden={onHidden}
					subscribeToTargetClick={subscribeToTargetClick}
				/>
			)}
		</PostOfficeMessage>
	);
};
